import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@/assets/icons/refresh-icon.svg?react';
import WarnIcon from '@/assets/icons/warn-icon.svg?react';
import { getMessageComponent } from '@/components/chat/Message';
import { type Message, MessageStatus } from '@/types/message.types';
import { formatMessageTime } from '@/utils/message';
import type { MessageGroupProps } from './types';

const renderMessage = (message: Message) => {
  const MessageComponent = getMessageComponent(message);
  return <MessageComponent message={message} />;
};

/**
 * MessageGroup - Renders message group component, contains group of messages with same direction and time,
 * message sender info, time, and message bubble
 */
export const MessageGroup = React.memo<MessageGroupProps>(
  ({
    messageGroup,
    agentDisplayName,
    agentAvatarCharacter,
    failedDropdownId,
    failedDropdownWidth,
    failedTooltipWidth,
    failedWarningRefs,
    hoverElPositions,
    handleRetryClick,
    handleFailedWarningClick,
  }) => {
    const { t } = useTranslation('common');
    const shadowRootRef = useRef<ShadowRoot>(null);
    const pilotMessage = useMemo(
      () => messageGroup[messageGroup.length - 1],
      [messageGroup],
    );

    useEffect(() => {
      shadowRootRef.current =
        document.getElementById('sleekflow-widget-container')?.shadowRoot ||
        null;
    }, []);

    // dynamically adding bubble shape classes based on message direction and number of messages in the group
    useEffect(() => {
      if (!shadowRootRef.current) return;

      const messageBubbleEls =
        shadowRootRef?.current?.querySelectorAll(
          `#message-group-${pilotMessage.id} .sf\\:message-bubble`,
        ) || [];

      // reset classes (in case of re-renders)
      messageBubbleEls.forEach((messageBubbleEl) => {
        messageBubbleEl.classList.remove(
          'sf:rounded-tl-sm',
          'sf:rounded-tr-sm',
          'sf:rounded-bl-sm',
          'sf:rounded-br-sm',
        );
      });

      const directionVariant = pilotMessage.isSentFromSleekflow ? 'l' : 'r';
      const singleMessageBubbleClasses = [`sf:rounded-t${directionVariant}-sm`];
      const firstMessageBubbleClasses = [`sf:rounded-b${directionVariant}-sm`];
      const lastMessageBubbleClasses = [`sf:rounded-t${directionVariant}-sm`];
      const middleMessageBubbleClasses = [
        `sf:rounded-b${directionVariant}-sm`,
        `sf:rounded-t${directionVariant}-sm`,
      ];

      if (messageBubbleEls.length === 1) {
        // single message bubble
        messageBubbleEls[0]?.classList.add(...singleMessageBubbleClasses);
      } else if (messageBubbleEls.length > 1) {
        // first message bubble
        messageBubbleEls[0]?.classList.add(...firstMessageBubbleClasses);
        // last message bubble
        messageBubbleEls[messageBubbleEls.length - 1]?.classList.add(
          ...lastMessageBubbleClasses,
        );
        // middle message bubbles
        for (let i = 1; i < messageBubbleEls.length - 1; i++) {
          messageBubbleEls[i]?.classList.add(...middleMessageBubbleClasses);
        }
      }
    }, [messageGroup, pilotMessage]);

    return (
      <div
        id={`message-group-${pilotMessage.id}`}
        className={`sf:flex ${pilotMessage.isSentFromSleekflow ? 'sf:justify-start' : 'sf:justify-end'}`}
      >
        <div className="sf:flex sf:items-start sf:gap-1 sf:max-w-[var(--w-message-bubble-full)]">
          {/* Agent avatar */}
          {pilotMessage.isSentFromSleekflow && (
            <figure className="sf:bg-widget-button-color sf:w-7 sf:h-7 sf:rounded-full sf:flex sf:items-center sf:justify-center sf:flex-shrink-0 sf:mt-5">
              <span className="sf:text-white sf:text-xs sf:font-medium">
                {agentAvatarCharacter}
              </span>
            </figure>
          )}

          <div
            className={`sf:flex sf:flex-col sf:gap-1 ${pilotMessage.isSentFromSleekflow ? 'sf:items-start' : 'sf:items-end'}`}
          >
            {/* Timestamp and sender info */}
            <p className="sf:flex sf:items-center sf:gap-2">
              {pilotMessage.isSentFromSleekflow && !!agentDisplayName && (
                <span className="sf:text-xs sf:text-gray-600">
                  {agentDisplayName}
                </span>
              )}

              {!!pilotMessage.timestamp && (
                <span className="sf:text-xs sf:text-gray-600">
                  {formatMessageTime(pilotMessage.timestamp)}
                </span>
              )}

              {!pilotMessage.isSentFromSleekflow &&
                pilotMessage.status &&
                pilotMessage.status !== MessageStatus.FAILED && (
                  <span className="sf:text-xs sf:text-gray-600">
                    {pilotMessage.status}
                  </span>
                )}
            </p>

            {messageGroup.map((message) => (
              <div
                id={`${message.id}`}
                key={message.id}
                className="sf:flex sf:gap-2"
              >
                {/* Failed warning and retry dropdown */}
                {!message.isSentFromSleekflow &&
                  message.status === MessageStatus.FAILED && (
                    <div
                      className="sf:flex sf:items-center sf:relative"
                      ref={(el) => {
                        if (el) {
                          failedWarningRefs?.current.set(message.id, el);
                        }
                      }}
                    >
                      <button
                        onClick={() => handleFailedWarningClick?.(message.id)}
                        className="sf:group sf:relative sf:cursor-pointer"
                      >
                        <WarnIcon className="sf:w-4 sf:h-4 sf:text-red-90" />
                        <span
                          className="sf:absolute sf:top-full sf:mt-2 sf:px-3 sf:py-2 sf:bg-gray-800 sf:text-white sf:text-xs sf:text-left sf:rounded sf:opacity-0 sf:group-hover:opacity-100 sf:transition-opacity sf:pointer-events-none sf:z-10"
                          style={{
                            width: `${failedTooltipWidth}px`,
                            left: hoverElPositions?.get(message.id)?.left,
                            right: hoverElPositions?.get(message.id)?.right,
                          }}
                        >
                          {t('messages.failedTooltip', {
                            reason: 'network error', // TODO: get the reason from the message
                          })}
                        </span>
                      </button>

                      {failedDropdownId === message.id && (
                        <div
                          className="sf:absolute sf:top-[calc(50%+10px)] sf:mt-1 sf:bg-white sf:rounded sf:shadow-lg sf:z-20"
                          style={{
                            width: `${failedDropdownWidth}px`,
                            left: hoverElPositions?.get(message.id)?.left,
                            right: hoverElPositions?.get(message.id)?.right,
                          }}
                        >
                          <button
                            onClick={() => handleRetryClick?.(message.id)}
                            className="sf:w-full sf:h-12 sf:px-3 sf:py-2 sf:flex sf:items-center sf:gap-3 sf:text-sm sf:text-gray-700 sf:hover:bg-widget-button-color-dimmed sf:rounded sf:transition-colors sf:cursor-pointer"
                          >
                            <RefreshIcon className="sf:w-4 sf:h-4 sf:text-widget-button-color" />
                            <span className="sf:text-widget-button-color sf:font-medium">
                              {t('messages.retryButton')}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Message bubbles */}
                <div
                  className={`sf:flex sf:flex-col sf:gap-1 ${message.isSentFromSleekflow ? 'sf:items-start' : 'sf:items-end'}`}
                >
                  {renderMessage(message)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

MessageGroup.displayName = 'MessageGroup';
